import React, { useCallback, useMemo, useState } from 'react';

import { Pagination } from 'components/pagination/Pagination';

import { useWheelOfFortuneQuery } from 'hooks/queries/useWheelOfFortuneQuery';

import WheelOfFortuneHistoryTableHeader from './WheelOfFortuneHistoryTableHeader';
import WheelOfFortuneHistoryTableRow from './WheelOfFortuneHistoryTableRow';

interface WheelOfFortuneHistoryTableProps {
  pageSize?: number;
}

const TABLE_COLUMNS: string[] = ['Date', 'Type', 'Reward', 'Event Name'];

/**
 * Layout only — no panel, no heading, no fixed width.
 *
 * This renders inside WheelOfFortuneHistoryModal, which already supplies the surface, the
 * title and the close button. Keeping its own rounded panel and <h3> here put a second
 * container and a second title inside the first. The old fixed `lg:w-[720px]` also fought
 * the modal's max-width, so width is left to the parent.
 */
const TABLE_CONTAINER_CLASSES =
  'tw-flex tw-w-full tw-flex-col tw-items-center tw-gap-[10px] tw-font-["Rajdhani"] lg:tw-gap-5';

const MESSAGE_CLASSES = 'tw-text-center tw-text-[18px] tw-font-bold tw-text-white';

const WheelOfFortuneHistoryTable: React.FC<WheelOfFortuneHistoryTableProps> = ({ pageSize = 10 }) => {
  const [currentPage, setCurrentPage] = useState(1);

  const { historyData, historyLoading, hasNextPage, hasPreviousPage, totalPages } = useWheelOfFortuneQuery({
    page: currentPage,
    pageSize,
  });

  const handleGoToPage = useCallback((page: number) => {
    setCurrentPage(page);
  }, []);

  const handleGoToNextPage = useCallback(() => {
    if (hasNextPage) {
      setCurrentPage((prev) => prev + 1);
    }
  }, [hasNextPage]);

  const handleGoToPreviousPage = useCallback(() => {
    if (hasPreviousPage) {
      setCurrentPage((prev) => prev - 1);
    }
  }, [hasPreviousPage]);

  const isEmpty = !historyData || historyData.results.length === 0;
  const showPagination = totalPages > 1;

  if (historyLoading) {
    return (
      <div className={TABLE_CONTAINER_CLASSES}>
        <div className={MESSAGE_CLASSES}>Loading...</div>
      </div>
    );
  }

  if (isEmpty) {
    return (
      <div className={TABLE_CONTAINER_CLASSES}>
        <div className={MESSAGE_CLASSES}>No history available</div>
      </div>
    );
  }

  return (
    <div className={TABLE_CONTAINER_CLASSES}>
      <WheelOfFortuneHistoryTableHeader columns={TABLE_COLUMNS} />
      <div className="tw-flex tw-w-[100%] tw-flex-col tw-gap-[10px] tw-self-stretch lg:tw-gap-[20px]">
        {historyData.results.map((item) => (
          <WheelOfFortuneHistoryTableRow key={`${item.created_at}-${item.type}-${item.source_name}`} item={item} />
        ))}
      </div>
      {showPagination && (
        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          hasNextPage={hasNextPage}
          hasPreviousPage={hasPreviousPage}
          variant="wheelOfFortune"
          goToPage={handleGoToPage}
          goToNextPage={handleGoToNextPage}
          goToPreviousPage={handleGoToPreviousPage}
        />
      )}
    </div>
  );
};

export default WheelOfFortuneHistoryTable;
